import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { queryClient } from '../../main';
import { useTenantCode } from './useTenantCode';
import {
    cancelarPorCliente,
    anadirConRecuperacion,
    fetchClientesConRecuperacion,
    CancelarPorClienteResponse,
} from '../api/asistenciaService';
import { formatearFecha } from '../../utils/dateHelpers';

// Tras cancelar o reservar cambian la clase, las recuperaciones del cliente y su
// historial: se refresca todo lo que depende de ello.
const refrescar = () => {
    queryClient.invalidateQueries({ queryKey: ['eventos'] });
    queryClient.invalidateQueries({ queryKey: ['client'] });
    queryClient.invalidateQueries({ queryKey: ['clients'] });
    queryClient.invalidateQueries({ queryKey: ['clientes-con-recuperacion'] });
};

const mensajeCancelacion = (data: CancelarPorClienteResponse) => {
    if (data.recoveryGranted) {
        const hasta = data.rescheduleDeadline ? ` (válida hasta el ${formatearFecha(data.rescheduleDeadline)})` : '';
        return `Clase cancelada. Ha ganado una recuperación${hasta}.`;
    }
    if (data.recoveryDeniedReason === 'late') {
        return `Clase cancelada SIN recuperación: faltaban menos de ${data.minCancellationHours} h.`;
    }
    if (data.recoveryDeniedReason === 'outside_horizon') {
        return `Clase cancelada SIN recuperación: faltaban más de ${data.cancellationHorizonDays} días.`;
    }
    return 'Clase cancelada.';
};

export const useCancelarPorClienteMutation = () => useMutation({
    mutationFn: ({ eventId, clientId }: { eventId: string; clientId: string }) => cancelarPorCliente(eventId, clientId),
    onSuccess: (data) => {
        refrescar();
        if (data.recoveryGranted) toast.success(mensajeCancelacion(data));
        else toast.warning(mensajeCancelacion(data), { autoClose: 8000 });
    },
    onError: (error: any) => {
        toast.error(error.response?.data?.message || 'No se pudo cancelar la clase');
    },
});

export const useAnadirConRecuperacionMutation = () => useMutation({
    mutationFn: ({ eventId, clientId }: { eventId: string; clientId: string }) => anadirConRecuperacion(eventId, clientId),
    onSuccess: () => {
        refrescar();
        toast.success('Cliente apuntado. Se ha usado una de sus recuperaciones.');
    },
    onError: (error: any) => {
        toast.error(error.response?.data?.message || 'No se pudo apuntar con recuperación');
    },
});

// Solo se pide al abrir el selector de "Añadir con recuperación".
export const useClientesConRecuperacionQuery = (eventId: string, enabled: boolean) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['clientes-con-recuperacion', eventId, tenantCode],
        queryFn: () => fetchClientesConRecuperacion(eventId),
        enabled: enabled && !!eventId,
    });
};
